import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.png";

/** NetSuite shows account IDs uppercased with underscores (`1234567_SB1`) but addresses them lowercased with dashes. */
const accountId = (domain: string) =>
  domain
    .toLowerCase()
    .replace(/\.(app|suitetalk\.api|restlets\.api)\.netsuite\.com$/, "")
    .replaceAll("_", "-");

const appHost = (domain: string) =>
  `https://${accountId(domain)}.app.netsuite.com`;

const restHost = (domain: string) =>
  `https://${accountId(domain)}.suitetalk.api.netsuite.com`;

const restletHost = (domain: string) =>
  `https://${accountId(domain)}.restlets.api.netsuite.com`;

export const netSuite = defineConnector({
  identifier: "netSuite",
  name: "NetSuite",
  tagLine: "ERP and business management",
  keywords: "oracle erp accounting financials suitetalk restlets",
  logo,
  protocol: "http",
  tags: ["data"],
  defaultUrlPattern: ({ domain }) =>
    `https://${domain ? accountId(domain).replace(/\./g, "\\.") : "[^/]*"}\\.(?:suitetalk|restlets)\\.api\\.netsuite\\.com/.*`,
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your NetSuite account ID",
      items: [
        {
          kind: "markdown",
          content:
            "Log in to NetSuite and find your account ID under **Setup > Company > Company Information**, or in the URL: `https://<account-id>.app.netsuite.com`. Sandbox accounts carry a suffix, e.g. `1234567_SB1`.",
        },
        {
          kind: "input",
          label: "Account ID",
          placeholder: "1234567_SB1",
          required: true,
          helperText: "Just the account ID, not the full URL.",
          target: { kind: "configuration", field: "domain" },
        },
      ],
    },
    {
      title: "Create an integration record in NetSuite",
      items: [
        {
          kind: "markdown",
          content:
            "In NetSuite, go to **Setup > Integration > Manage Integrations > New**.\n\nConfigure the integration with these settings:\n- **Name**: Choose a descriptive name (e.g., '3B Integration')\n- **State**: Enabled\n- **Authentication**: OAuth 2.0 flow\n- **Token-Based Authentication**: Checked\n- **Authorization Code Grant**: Checked",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content:
            "In the **Redirect URI** field of your integration record, add the following URL:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/netSuite/callback",
        },
      ],
    },
    {
      title: "Configure scopes and save",
      items: [
        {
          kind: "markdown",
          content:
            "In the **Scope** section, select the scopes for your integration. Common scopes include:\n- `restlets` – Access to RESTlets\n- `rest_webservices` – Access to REST web services\n\nEnter the same scopes below as a space-separated list, then click **Save** in NetSuite and note the **Consumer Key** and **Consumer Secret**.",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "rest_webservices restlets",
          defaultValue: "rest_webservices restlets",
          helperText:
            "Space-separated list of scopes. Must match what you selected in NetSuite.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "markdown",
          content:
            "From your integration record, copy the **Consumer Key** and **Consumer Secret**.",
        },
        {
          kind: "input",
          label: "Consumer Key",
          placeholder: "Paste your Consumer Key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Consumer Secret",
          placeholder: "Paste your Consumer Secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your NetSuite account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your NetSuite account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "domain" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ domain }) =>
      `${appHost(domain)}/app/login/oauth2/authorize.nl`,
    tokenEndpoint: ({ domain }) =>
      `${restHost(domain)}/services/rest/auth/oauth2/v1/token`,
    tokenEndpointAuthMethod: () => "client_secret_basic",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ domain }) =>
      `${restHost(domain)}/services/rest/record/v1/metadata-catalog`,
  },
  publicEnvVars: ({ domain }): Record<string, string> => {
    if (domain) {
      return {
        NETSUITE_URL: restHost(domain),
        NETSUITE_RESTLET_URL: restletHost(domain),
      };
    }
    return {};
  },
});
